import React from "react";
import clsx from "clsx";

import { ButtonLink } from "@app/core/button-link/button-link";
import { CtaCard, CtaCardProps } from "@app/core/cta-card/cta-card";

import styles from "./featured-layout.module.scss";
import Typography from "../typography";

export type FeaturedLayoutProps<T> = {
  title?: string;
  subtitle?: string;
  items: T[];
  renderItem: (item: T) => React.ReactNode;
  topCtaTitle?: {
    title: string;
    url: string;
  };
  ctaCard?: CtaCardProps;
  mobileMaxItemAmt?: number;
};

const AMOUNT_OF_ITEMS_DESKTOP = 5;
const AMOUNT_OF_ITEMS_MOBILE = 8;

const FeaturedLayout = <T extends Record<string, any>>({
  items,
  renderItem,
  title,
  subtitle,
  topCtaTitle,
  ctaCard,
  mobileMaxItemAmt
}: FeaturedLayoutProps<T>) => {
  const { getHiddenClasses } = useGetIsItemHidden(items.length, mobileMaxItemAmt);

  return (
    <div className="uk-container">
      <div className={styles.root}>
        <div className={styles.top}>
          {(title || subtitle) && (
            <div className={styles.title}>
              {title && <Typography tag="h2">{title}</Typography>}
              {subtitle && <span className={styles.subtitle}>{subtitle}</span>}
            </div>
          )}
          {topCtaTitle?.title && (
            <ButtonLink href={topCtaTitle.url} className={styles.topCta}>
              {topCtaTitle.title}
            </ButtonLink>
          )}
        </div>
        {items && items.length > 0 && (
          <div className={styles.grid}>
            {items.map((item, index) => (
              <div key={index} className={clsx(styles.item, ctaCard?.href && getHiddenClasses(index + 1))}>
                {renderItem(item)}
              </div>
            ))}
            {ctaCard?.href && <CtaCard {...ctaCard} />}
          </div>
        )}
      </div>
    </div>
  );
};

export { FeaturedLayout };

const useGetIsItemHidden = (itemAmt: number, mobileMaxAmt?: number) => {
  const amountOfRows = Math.ceil((itemAmt + 1) / AMOUNT_OF_ITEMS_DESKTOP);

  const maxAmountOfItems = {
    1200: amountOfRows * 4 - 1,
    959: amountOfRows * 3 - 1,
    768: 3,
    640: mobileMaxAmt || AMOUNT_OF_ITEMS_MOBILE
  };

  const getHiddenClasses = (index: number) => {
    const classes: string[] = [];
    if (index > maxAmountOfItems["1200"]) classes.push(styles["hide-1200"]);
    if (index > maxAmountOfItems["959"]) classes.push(styles["hide-959"]);
    if (index > maxAmountOfItems["768"]) classes.push(styles["hide-768"]);
    if (index > maxAmountOfItems["640"]) classes.push(styles["hide-640"]);

    return classes;
  };

  return {
    getHiddenClasses
  };
};
